import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, Spin, Tag } from 'antd';
import {
  DesktopOutlined,
  CheckCircleOutlined,
  UserDeleteOutlined,
  LogoutOutlined,
  KeyOutlined,
  ThunderboltOutlined,
  HistoryOutlined,
  CalculatorOutlined,
  TeamOutlined,
  ExportOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import ApproveList from './pages/ApproveList';
import HistoryList from './pages/HistoryList';
import BlackList from './pages/BlackList';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import AutoApprove, { AutoApproveHistory } from './pages/AutoApprove';
import PromoRules from './pages/PromoRules';
import Users from './pages/Users';
import ProtectedRoute from './components/ProtectedRoute';
import api, { ROLES, ROLE_LABELS, clearSession } from './api';
import './App.css';

const { Header, Content, Sider } = Layout;

const ALL = [ROLES.ADMIN, ROLES.STAFF];
const ADMIN_ONLY = [ROLES.ADMIN];

const PAGES = [
  { path: '/', label: 'Đơn chờ phê duyệt', icon: <DesktopOutlined />, roles: ALL, element: () => <ApproveList /> },
  { path: '/history', label: 'Đơn đã phê duyệt', icon: <CheckCircleOutlined />, roles: ALL, element: () => <HistoryList /> },
  { path: '/promo-rules', label: 'Cấu hình khuyến mãi', icon: <CalculatorOutlined />, roles: ADMIN_ONLY, element: () => <PromoRules /> },
  { path: '/auto-approve', label: 'Mã duyệt tự động', icon: <ThunderboltOutlined />, roles: ADMIN_ONLY, element: () => <AutoApprove /> },
  { path: '/auto-approve-history', label: 'Đơn đã duyệt tự động', icon: <HistoryOutlined />, roles: ADMIN_ONLY, element: () => <AutoApproveHistory /> },
  { path: '/blacklist', label: 'Danh sách đen', icon: <UserDeleteOutlined />, roles: ADMIN_ONLY, element: () => <BlackList /> },
  { path: '/users', label: 'Quản lý tài khoản', icon: <TeamOutlined />, roles: ADMIN_ONLY, element: (user) => <Users currentUser={user} /> },
  { path: '/change-password', label: 'Đổi mật khẩu', icon: <KeyOutlined />, roles: ALL, element: () => <ChangePassword /> },
];

const EXTERNAL_LINKS = [
  { key: 'export', href: 'https://db.83868668.com/', label: 'XUẤT FILE', icon: <ExportOutlined />, roles: ADMIN_ONLY },
  { key: 'ttkm', href: 'https://khuyenmai-gk88.pages.dev/admin/', label: 'CÀI ĐẶT GIAO DIỆN TTKM', icon: <SettingOutlined />, roles: ADMIN_ONLY },
];

function SidebarMenu({ role }) {
  const location = useLocation();
  const items = [
    ...PAGES.filter((p) => p.roles.includes(role)).map((p) => ({
      key: p.path,
      icon: p.icon,
      label: <Link to={p.path}>{p.label}</Link>,
    })),
    ...EXTERNAL_LINKS.filter((l) => l.roles.includes(role)).map((l) => ({
      key: l.key,
      icon: l.icon,
      label: <a href={l.href}>{l.label}</a>,
    })),
  ];

  return <Menu
    theme="light"
    mode="inline"
    selectedKeys={[location.pathname]}
    items={items}
    style={{ background: '#FAF0E6' }}
  />;
}

function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(() => !!localStorage.getItem('admin_token'));
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;
    api.post('/auth/verify', { token })
      .then((res) => {
        if (res.data?.status) {
          setUser(res.data.data);
        } else {
          clearSession();
        }
      })
      .catch(() => clearSession())
      .finally(() => setChecking(false));
  }, []);

  const handleLogin = (loggedInUser) => {
    setUser(loggedInUser);
    navigate('/');
  };

  const handleLogout = () => {
    clearSession();
    setUser(null);
    navigate('/login');
  };

  if (checking) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin size="large" /></div>;
  }

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider collapsible style={{ background: '#FFF5EE' }}>
        <div
          className="logo"
          style={{
            height: 64,
            margin: '16px 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <img
            src="/logogk88.png"
            alt="Logo"
            style={{
              height: '100%',
              maxWidth: '90%',
              objectFit: 'contain'
            }}
          />
        </div>
        <SidebarMenu role={user.role} />
      </Sider>
      <Layout>
        <Header style={{ background: '#fff', padding: '0 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 24 }}>Quản trị Đơn khuyến mãi</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ color: '#666', fontSize: 14 }}>
              Xin chào, <strong>{user.username}</strong>
              {user.full_name && user.full_name !== user.username && (
                <span style={{ color: '#999', fontSize: 12, marginLeft: 4 }}>
                  ({user.full_name})
                </span>
              )}
              <Tag color={user.role === ROLES.ADMIN ? 'red' : 'blue'} style={{ marginLeft: 8 }}>
                {ROLE_LABELS[user.role] || user.role}
              </Tag>
            </span>
            <Button
              type="text"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              style={{ color: '#666' }}
            >
              Đăng xuất
            </Button>
          </div>
        </Header>
        <Content style={{ margin: '16px' }}>
          <Routes>
            {PAGES.map((p) => (
              <Route
                key={p.path}
                path={p.path}
                element={<ProtectedRoute user={user} roles={p.roles}>{p.element(user)}</ProtectedRoute>}
              />
            ))}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  );
}

export default App;
